import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Button, Drawer, notification, Collapse } from 'antd';
import { BellOutlined, CloseOutlined } from '@ant-design/icons';
import { useTaskStore } from '../store/taskStore';
import { InternalNotificationList } from './InternalNotificationList';
import { BackgroundTaskList } from './BackgroundTaskList';
import './NotificationButton.css';

export const NotificationButton: React.FC = () => {
  const { tasks, activeTaskId, backgroundedTaskIds, removeTask } = useTaskStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationListRef = useRef<any>(null);
  
  const [notifiedTasks, setNotifiedTasks] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('notified-tasks');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    localStorage.setItem('notified-tasks', JSON.stringify(Array.from(notifiedTasks)));
  }, [notifiedTasks]);

  const purgeTask = useCallback((task: any) => {
    if (task.status === 'generating' && task.requestId && window.UzySDK?.cancelSystemAgent) {
      window.UzySDK.cancelSystemAgent(task.requestId);
    }
    if (task.requestId && window.UzySDK?.destroyTask) {
      window.UzySDK.destroyTask(task.requestId);
    }
    removeTask(task.id);
  }, [removeTask]);

  useEffect(() => {
    const sixHoursAgo = Date.now() - 6 * 60 * 60 * 1000;
    tasks.forEach((task) => {
      if (task.createdAt < sixHoursAgo) {
        purgeTask(task);
      }
    });
  }, [tasks, purgeTask]);

  const backgroundTasks = tasks.filter(
    (t) => t.id !== activeTaskId && backgroundedTaskIds.has(t.id)
  );

  useEffect(() => {
    backgroundTasks.forEach((task) => {
      if (task.status === 'done' && !notifiedTasks.has(task.id)) {
        notification.success({
          message: '任务完成',
          description: task.resourceName,
          duration: 3,
          onClick: () => {
            if (task.requestId && window.UzySDK?.showTask) {
              window.UzySDK.showTask(task.requestId);
            }
            setDrawerOpen(false);
          },
        });
        setNotifiedTasks((prev) => new Set(prev).add(task.id));
      }
    });
  }, [backgroundTasks, notifiedTasks]);

  const handleView = (task: any) => {
    if (task.requestId && window.UzySDK?.showTask) {
      window.UzySDK.showTask(task.requestId);
    }
    setDrawerOpen(false);
  };

  return (
    <>
      <div className="task-button-wrapper">
        <Badge count={backgroundTasks.length + unreadCount} offset={[-5, 5]}>
          <Button
            type="text"
            icon={<BellOutlined />}
            onClick={() => setDrawerOpen(true)}
            className="apple-header-icon-btn"
          />
        </Badge>
      </div>

      <Drawer
        title="消息中心"
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        size="default"
        styles={{ body: { padding: '12px' }, header: { padding: '12px 16px' } }}
        closeIcon={false}
        extra={
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setDrawerOpen(false)}
          />
        }
      >
        <Collapse
          defaultActiveKey={[]}
          ghost
          items={[
            {
              key: 'notifications',
              label: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Badge count={unreadCount} offset={[10, 0]}>
                    <span>站内通知{unreadCount > 0 ? `(${unreadCount})` : ''}</span>
                  </Badge>
                  {unreadCount > 0 && (
                    <Button
                      type="link"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        notificationListRef.current?.handleMarkAllAsRead();
                      }}
                      style={{ marginRight: 8 }}
                    >
                      全部已读
                    </Button>
                  )}
                </div>
              ),
              children: (
                <InternalNotificationList 
                  ref={notificationListRef}
                  unreadCount={unreadCount}
                  onUnreadCountChange={setUnreadCount}
                />
              ),
            },
            ...(backgroundTasks.length > 0 ? [{
              key: 'tasks',
              label: (
                <Badge count={backgroundTasks.length} offset={[10, 0]}>
                  <span>后台任务{backgroundTasks.length > 0 ? `(${backgroundTasks.length})` : ''}</span>
                </Badge>
              ),
              children: (
                <BackgroundTaskList
                  tasks={backgroundTasks}
                  onView={handleView}
                  onClose={purgeTask}
                  onDrawerClose={() => setDrawerOpen(false)}
                />
              ),
            }] : []),
          ]}
        />
      </Drawer>
    </>
  );
};
